import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import UrgencySignalWrapper from "../urgency-signal/wrapper";
import Pill from "./pill";
import { useAppSelector } from "../../../../../../util/store-types";
import { isOneSize } from "../helpers/disco-product-helper";
import { findPreferredSkuObj } from "../helpers/size-selection-helper";

const SizePills = ({
  onSelect,
  skus,
  availability,
  availabilityLoaded,
  availabilityError,
  hideOneSize,
  selectUnavailableSize,
  noPreselect = false,
  label = "Size",
  errorSku,
  dataTestId = "size-pills-container",
}) => {
  const [selectedSize, setSelectedSize] = useState(null);
  const canonicalSize = useAppSelector(state => state.workingFilters?.canonicalSizes);
  const buyBoxMode = useAppSelector(state => state.pdpBuyBoxMode);
  const userProfileHasLoaded = useAppSelector(state => state.userData?.userProfileHasLoaded);
  const userPrimarySize = useAppSelector(state => state.userData?.userProfile?.profiles?.primarySize ?? "");
  // Check if the product has only one SKU and that SKU is labeled "One Size"
  const isOneSizeOnly = isOneSize(skus);
  // Hide "One Size" display only for accessories
  const shouldHideOneSize = hideOneSize && isOneSizeOnly;

  const handleSelect = size => {
    setSelectedSize(size);
    if (onSelect) onSelect(size);
  };

  // Autoselect if oneSize product
  if (isOneSizeOnly && !selectedSize) {
    handleSelect(skus[0]);
  }

  useEffect(() => {
    // If we're still loading availability, or there's no valid availability data,
    // or a size has already been selected, or it's a one-size-only item,
    // then we don't need to automatically select a size based on preferences.
    if (
      noPreselect ||
      !availabilityLoaded ||
      !Array.isArray(availability) ||
      availability.length === 0 ||
      selectedSize ||
      isOneSizeOnly
    ) {
      return; // Exit early, no automatic selection needed
    }

    let preferredSize;

    // Prioritize canonical size if available and valid
    // If secon size, do not auto select
    if (Array.isArray(canonicalSize) && canonicalSize?.length > 0) {
      preferredSize = findPreferredSkuObj(availability, canonicalSize, skus);
      // If no canonical size, try user's primary size if profile loaded
    } else if (userProfileHasLoaded && userPrimarySize) {
      preferredSize = findPreferredSkuObj(availability, [userPrimarySize], skus);
    }
    if (!preferredSize && selectUnavailableSize) {
      if (Array.isArray(canonicalSize) && canonicalSize?.length > 0) {
        preferredSize = findPreferredSkuObj(availability, canonicalSize, skus, true);
        // If no canonical size, try user's primary size if profile loaded
      } else if (userProfileHasLoaded && userPrimarySize) {
        preferredSize = findPreferredSkuObj(availability, [userPrimarySize], skus, true);
      }
    }

    if (preferredSize) {
      handleSelect(preferredSize);
    }
  }, [
    Array.isArray(canonicalSize) && canonicalSize?.length > 0,
    userProfileHasLoaded,
    userPrimarySize,
    availabilityLoaded,
    Array.isArray(availability) && availability?.length > 0,
    !!selectedSize,
    isOneSizeOnly,
  ]);

  return (
    <div data-test-id={dataTestId}>
      <div className={`flex justify-between ${shouldHideOneSize && isOneSizeOnly ? "mb-6" : "mb-3"}`}>
        <div>
          <span className="font-medium">{label}: </span> &nbsp;
          <span>{selectedSize?.sizeDisplayText ? `${selectedSize?.sizeDisplayText}` : ""}</span>
        </div>

        {buyBoxMode !== "reserve" && (
          <UrgencySignalWrapper selectedSku={selectedSize?.sku} availability={availability} />
        )}
      </div>

      <p className="mb-3 text-mutedRed">{availabilityError}</p>
      {!(shouldHideOneSize && isOneSizeOnly) && (
        <div className="flex flex-wrap gap-3 mb-6" data-test-id="size-pills">
          {skus?.map(size => (
            <Pill
              key={size?.sku}
              availabilityLoaded={!!availabilityLoaded}
              availability={availability}
              sizeToRender={size}
              error={size.sku && size.sku === errorSku}
              selectedSize={selectedSize}
              handleSelect={handleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

SizePills.propTypes = {
  onSelect: PropTypes.func,
  skus: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string,
      sizeDisplayText: PropTypes.string,
    })
  ).isRequired,
  availability: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string,
      availableCount: PropTypes.number,
    })
  ).isRequired,
  availabilityLoaded: PropTypes.bool,
  availabilityError: PropTypes.string,
  hideOneSize: PropTypes.bool,
  selectUnavailableSize: PropTypes.bool,
  noPreselect: PropTypes.bool,
  label: PropTypes.string,
  errorSku: PropTypes.string,
  dataTestId: PropTypes.string,
};

export default SizePills;
